import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store/store';
import { selectCurrentUser } from '../features/auth/authSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { fetchImplementationGuideFull } from '../features/iso9001/implementation/isoImplementationApi';
import { invalidateImplementationSummary } from '../features/iso9001/implementation/isoImplementationSlice';
import type {
  ImplementationGuideDetail,
  OrganizationItemContextView,
  TaskWithProgressView,
} from '../features/iso9001/implementation/types';
import { DeliverableRow } from '../features/iso9001/implementation/IsoDeliverablesGrid';
import {
  ISO_IMPLEMENTATION_META,
  type IsoImplementationStandard,
} from '../features/iso9001/implementation/isoImplementationConstants';
import { IsoImplementationUnavailable } from '../features/iso9001/implementation/IsoImplementationUnavailable';
import { getApiErrorMessage } from '../features/iso9001/implementation/isoImplementationError';

export function IsoImplementationDeliverablesPage({
  standard,
}: {
  standard: IsoImplementationStandard;
}) {
  const meta = ISO_IMPLEMENTATION_META[standard];
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectCurrentUser);
  const isSuperadmin = user?.role === 'superadmin';
  const { canEdit } = usePlatformAccessLevel('iso_implementation.access');
  const [guide, setGuide] = useState<ImplementationGuideDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchImplementationGuideFull('entregables', standard);
      setGuide(data);
    } catch (err) {
      setError(getApiErrorMessage(err) ?? 'No se pudieron cargar los entregables.');
    } finally {
      setLoading(false);
    }
  }, [standard]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTaskUpdated = (updatedTask: TaskWithProgressView) => {
    setGuide((current) => {
      if (!current) return current;
      return {
        ...current,
        phases: current.phases.map((phase) => ({
          ...phase,
          tasks: phase.tasks.map((task) =>
            task.id === updatedTask.id ? updatedTask : task,
          ),
        })),
      };
    });
    dispatch(invalidateImplementationSummary(standard));
  };

  const handleContextUpdated = (
    taskId: string,
    context: OrganizationItemContextView,
  ) => {
    setGuide((current) => {
      if (!current) return current;
      return {
        ...current,
        phases: current.phases.map((phase) => ({
          ...phase,
          tasks: phase.tasks.map((task) =>
            task.id === taskId ? { ...task, organizationContext: context } : task,
          ),
        })),
      };
    });
    dispatch(invalidateImplementationSummary(standard));
  };

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Cargando entregables…</p>;
  }

  if (error || !guide) {
    return (
      <IsoImplementationUnavailable
        standard={standard}
        guideSlug="entregables"
        title="No se pudieron cargar los entregables."
        detail={error}
      />
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <header className="border-b border-border pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {meta.label} · Implementación
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">{guide.title}</h1>
      </header>

      {guide.phases.map((phase) => (
        <section key={phase.id} className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border bg-muted/40 px-4 py-3">
            <h2 className="font-semibold">{phase.title}</h2>
            <p className="text-xs text-muted-foreground">{phase.tasks.length} entregables</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">✓</th>
                  <th className="px-3 py-2">Entregable</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Cláusula</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Responsable</th>
                  <th className="px-3 py-2">Aplica</th>
                  <th className="px-3 py-2">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {phase.tasks.map((task) => (
                  <DeliverableRow
                    key={task.id}
                    task={task}
                    canEdit={canEdit}
                    isSuperadmin={isSuperadmin}
                    onTaskUpdated={handleTaskUpdated}
                    onContextUpdated={handleContextUpdated}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
